import {
  Calculate as CalculateIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import {
  Alert,
  AlertTitle,
  Box,
  Chip,
  Paper,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import { getUser } from 'lib/api';
import { getCreditLineByProduct } from 'lib/api/creditLine';
import {
  calculateRiskPremium,
  RiskPremiumCalculationResult,
  RiskPremiumRates,
} from 'lib/functions/riskPremiumCalculator';
import { formatMoney } from 'lib/functions/utils';
import { useConversionRates } from 'lib/hooks/useConvertionRates';
import { SubProductCreditLineConcentration } from 'lib/types/models';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'services/redux/store';

type SubProduct = SubProductCreditLineConcentration;

interface RiskPremiumCalculation {
  amount: number;
  days: number;
  quotedCurrency: string;
  type: string;
  warrantyRates?: RiskPremiumRates;
}

const useCreditLineData = () => {
  const dispatch = useDispatch();
  const auth = useSelector((state: RootState) => state.auth);
  const [subProductsData, setSubProductsData] = useState<SubProduct[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchCreditLineData = async () => {
    try {
      setIsLoading(true);
      const profile = await getUser(dispatch, { id: auth.user?.id });
      if (!profile) throw new Error('Profile not found');
      const businessIdentifier = profile.company.taxIdentifier;
      const creditLine = await getCreditLineByProduct(
        dispatch,
        businessIdentifier,
        'sgr',
      );
      const data = creditLine?.subProducts || creditLine || [];
      setSubProductsData(data);
    } catch (error) {
      setError(error instanceof Error ? error.message : 'An error occurred');
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };
  return {
    subProductsData,
    isLoading,
    error,
    fetchCreditLineData,
  };
};

const useGetSubProductConcentration = (
  type: string,
  amount: number,
  days: number,
  quotedCurrency: string,
  warrantyRates?: RiskPremiumRates,
) => {
  const dispatch = useDispatch();
  const { subProductsData, isLoading, error, fetchCreditLineData } =
    useCreditLineData();
  const { fetchAndUpdateRates } = useConversionRates(dispatch);
  const [totalPremium, setTotalPremium] = useState(0);
  const [calculationResult, setCalculationResult] =
    useState<RiskPremiumCalculationResult | null>(null);
  const [hasInitialized, setHasInitialized] = useState(false);

  useEffect(() => {
    if (!hasInitialized) {
      void fetchAndUpdateRates();
      setHasInitialized(true);
    }
  }, [fetchAndUpdateRates, hasInitialized]);

  useEffect(() => {
    const performCalculation = () => {
      if (
        !amount ||
        !quotedCurrency ||
        !type ||
        typeof days !== 'number' ||
        days < 0
      ) {
        setTotalPremium(0);
        setCalculationResult(null);
        return;
      }

      const calculationBreakdown = calculateRiskPremium({
        quotedAmount: amount,
        days,
        quotedCurrency,
        type,
        rates: warrantyRates,
        subProductsData,
        useConcentrationRates: !warrantyRates,
      });

      setTotalPremium(calculationBreakdown?.amount ?? 0);
      setCalculationResult(calculationBreakdown);
    };

    performCalculation();
  }, [amount, days, quotedCurrency, type, warrantyRates, subProductsData]);

  return {
    isLoading,
    error,
    totalPremium,
    calculationResult,
    fetchCreditLineData,
  };
};

const RiskPremiumCalculator: React.FC<RiskPremiumCalculation> = ({
  amount,
  days,
  quotedCurrency,
  type,
  warrantyRates,
}) => {
  const { isLoading, totalPremium, fetchCreditLineData } =
    useGetSubProductConcentration(
      type,
      amount,
      days,
      quotedCurrency,
      warrantyRates,
    );

  useEffect(() => {
    void fetchCreditLineData();
  }, []);

  if (isLoading) {
    return (
      <Paper sx={{ p: 1.5, boxShadow: 'none', border: '1px solid #e0e0e0' }}>
        <Skeleton variant="rectangular" height={150} sx={{ borderRadius: 1 }} />
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 1.5, boxShadow: 'none', border: '1px solid #e0e0e0' }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
        <Box
          sx={{
            backgroundColor: 'primary.main',
            borderRadius: '50%',
            p: 0.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
          }}
        >
          <CalculateIcon sx={{ fontSize: '1rem' }} />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: 'text.primary',
              fontSize: '1rem',
              lineHeight: 1.1,
            }}
          >
            Cálculo de costo de emisión
          </Typography>
          <Chip
            label="SGR"
            size="small"
            color="primary"
            variant="outlined"
            sx={{ height: '18px', fontSize: '0.65rem' }}
          />
        </Box>
        {totalPremium > 0 && (
          <Box sx={{ textAlign: 'right' }}>
            <Stack
              direction="row"
              alignItems="start"
              justifyContent="flex-end"
              spacing={0.75}
            >
              <Typography variant="h4" color="primary">
                {formatMoney(totalPremium)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                CLP
              </Typography>
            </Stack>
          </Box>
        )}
      </Stack>
      <Alert
        severity="info"
        icon={<InfoIcon sx={{ fontSize: '0.9rem' }} />}
        sx={{
          borderRadius: 0.5,
          mt: 1.5,
          backgroundColor: 'rgba(25, 118, 210, 0.08)',
          border: '1px solid rgba(25, 118, 210, 0.2)',
          py: 0.75,
          '& .MuiAlert-message': { py: 0.25 },
        }}
      >
        <AlertTitle sx={{ fontWeight: 600, fontSize: '0.75rem' }}>
          Información Importante
        </AlertTitle>
        <Typography
          variant="body2"
          sx={{ fontSize: '0.75rem', lineHeight: 1.2 }}
        >
          {warrantyRates
            ? 'Este cálculo utiliza las tasas específicas de la garantía.'
            : 'Este es un valor referencial. El monto total a pagar puede variar al momento de hacer efectiva la solicitud de la garantía. Ingresar un monto de garantía diferente al monto cotizado puede afectar el costo total.'}
        </Typography>
      </Alert>
    </Paper>
  );
};

export default RiskPremiumCalculator;
